import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Badge,
  Chip,
  TextField,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface VideoUploadModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  selectedFolder: { id: string; name: string } | null;
  onUploadComplete?: () => void;
}

interface VideoFile {
  file: File;
  name: string;
  id: string;
}

const MAX_VIDEOS = 5;
const MAX_FILE_SIZE_MB = 50;

const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
  open,
  onClose,
  companyId,
  selectedFolder,
  onUploadComplete
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideos, setSelectedVideos] = useState<VideoFile[]>([]);
  const [editingVideoId, setEditingVideoId] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const totalVideos = selectedVideos.length + files.length;
    if (totalVideos > MAX_VIDEOS) {
      setError(isTr
        ? `En fazla ${MAX_VIDEOS} video yükleyebilirsiniz. Şu anda ${selectedVideos.length} videonuz seçili.`
        : `You can upload up to ${MAX_VIDEOS} videos. You currently selected ${selectedVideos.length} videos.`);
      return;
    }

    const validVideos: VideoFile[] = [];

    files.forEach(file => {
      if (file.type !== 'video/mp4') {
        setError(`${file.name}: ${tt('Sadece MP4 formatı desteklenmektedir.', 'Only MP4 format is supported.')}`);
        return;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`${file.name}: ${tt('Dosya boyutu', 'File size')} ${MAX_FILE_SIZE_MB}MB ${tt("dan küçük olmalıdır.", 'must be smaller.')}`);
        return;
      }

      const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9\sÇĞÖŞÜçğöşü\-_]/g, " ").trim();

      validVideos.push({
        file,
        name: cleanName || `${tt('Video', 'Video')} ${selectedVideos.length + validVideos.length + 1}`,
        id: generateId()
      });
    });

    if (validVideos.length > 0) {
      setSelectedVideos(prev => [...prev, ...validVideos]);
    }

    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeVideo = (id: string) => {
    setSelectedVideos(prev => prev.filter(v => v.id !== id));
    setEditingVideoId(null);
  };

  const updateVideoName = (id: string, newName: string) => {
    setSelectedVideos(prev =>
      prev.map(v =>
        v.id === id ? { ...v, name: newName.trim() || `Video ${prev.indexOf(v) + 1}` } : v
      )
    );
  };

  const handleUpload = async () => {
    if (selectedVideos.length === 0 || !companyId) return;

    const emptyNames = selectedVideos.filter(v => !v.name.trim());
    if (emptyNames.length > 0) {
      setError(tt('Lütfen tüm videolar için isim giriniz.', 'Please enter a name for all videos.'));
      return;
    }

    setUploading(true);
    setError(null);

    try {
      if (!currentUser) {
        throw new Error(tt('Kullanıcı oturumu açık değil', 'User session is not active'));
      }

      const { ImageUploadService } = await import('./services/imageUploadService');
      const files = selectedVideos.map(v => v.file);
      const uploadedVideos = await ImageUploadService.uploadVideos(files);

      const { ImageLibraryImageService } = await import('./services/imageLibraryService');

      const createPromises = selectedVideos.map(async (videoFile, index) => {
        const uploaded = uploadedVideos[index];

        return await ImageLibraryImageService.createImage({
          companyId,
          folderId: selectedFolder?.id || undefined,
          name: videoFile.name.trim(),
          url: uploaded.originalUrl,
          thumbnailUrl: uploaded.thumbnailUrl || '',
          size: videoFile.file.size,
          type: videoFile.file.type,
          mediaType: 'video',
          createdBy: currentUser.uid,
        });
      });

      await Promise.all(createPromises);

      if (onUploadComplete) {
        await onUploadComplete();
      }

      setSelectedVideos([]);
      setError(null);
      await new Promise(resolve => setTimeout(resolve, 100));
      onClose();

    } catch (error: any) {
      console.error('Video yükleme hatası:', error);
      setError(error.message || tt('Videolar yüklenirken bir hata oluştu', 'An error occurred while uploading videos'));
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setSelectedVideos([]);
    setError(null);
    setEditingVideoId(null);
    onClose();
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (open) {
      setSelectedVideos([]);
      setError(null);
      setEditingVideoId(null);
    }
  }, [open]);

  const canAddMore = selectedVideos.length < MAX_VIDEOS;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={!uploading ? handleClose : undefined}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'secondary.main',
          color: 'white',
          fontWeight: 'bold',
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VideoLibraryIcon sx={{ mr: 1 }} />
          {tt('Video Yükle', 'Upload Video')}
          <Badge badgeContent={selectedVideos.length} color="primary" sx={{ ml: 2 }}>
            <Chip
              label={`${selectedVideos.length}/${MAX_VIDEOS}`}
              size="small"
              sx={{ bgcolor: 'white', color: 'secondary.main' }}
            />
          </Badge>
        </Box>
        <IconButton onClick={handleClose} disabled={uploading} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {selectedFolder ? (
              <>
                <FolderIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  {tt('Hedef Klasör', 'Target Folder')}: {selectedFolder.name}
                </Typography>
              </>
            ) : (
              <>
                <HomeIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  {tt('Hedef: Ana Dizin', 'Target: Main Directory')}
                </Typography>
              </>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {tt('Videolar bu konuma yüklenecektir.', 'Videos will be uploaded to this location.')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {canAddMore && (
          <Paper
            elevation={1}
            sx={{
              p: 3,
              mb: 3,
              border: '2px dashed',
              borderColor: selectedVideos.length > 0 ? 'secondary.main' : 'grey.300',
              bgcolor: selectedVideos.length > 0 ? 'secondary.light' : 'grey.50',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'secondary.main',
                bgcolor: 'secondary.light'
              }
            }}
            onClick={handleSelectFileClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4"
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <VideoFileIcon
                sx={{
                  fontSize: 48,
                  color: selectedVideos.length > 0 ? 'secondary.main' : 'grey.400',
                  mb: 2
                }}
              />
              <Typography variant="h6" gutterBottom>
                {selectedVideos.length > 0
                  ? `${selectedVideos.length} ${tt('video seçildi', 'videos selected')}`
                  : tt('Video Seç (Çoklu seçim yapabilirsiniz)', 'Select Video (multiple selection allowed)')
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tt('Sadece MP4 formatı desteklenmektedir', 'Only MP4 format is supported')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {tt('Maksimum dosya boyutu', 'Maximum file size')}: {MAX_FILE_SIZE_MB}MB | {tt('Maksimum video sayısı', 'Maximum video count')}: {MAX_VIDEOS}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tt('Kalan slot', 'Remaining slot')}: {MAX_VIDEOS - selectedVideos.length}
              </Typography>
            </Box>
          </Paper>
        )}

        {!canAddMore && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {tt('Maksimum video sayısına ulaştınız', 'You reached the maximum video count')} ({MAX_VIDEOS}).
          </Alert>
        )}

        {selectedVideos.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {tt('Seçilen Videolar', 'Selected Videos')} ({selectedVideos.length})
            </Typography>
            <Grid container spacing={2}>
              {selectedVideos.map((videoFile, index) => (
                <Grid item xs={12} sm={6} md={4} key={videoFile.id}>
                  <Card elevation={2}>
                    <Box sx={{
                      height: 160,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100'
                    }}>
                      <VideoLibraryIcon sx={{ fontSize: 64, color: 'secondary.main' }} />
                    </Box>
                    <CardContent sx={{ pb: 1 }}>
                      {editingVideoId === videoFile.id ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={videoFile.name}
                          onChange={(e) => updateVideoName(videoFile.id, e.target.value)}
                          onBlur={() => setEditingVideoId(null)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') setEditingVideoId(null);
                          }}
                          autoFocus
                          placeholder={tt('Video adı giriniz...', 'Enter video name...')}
                        />
                      ) : (
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ cursor: 'pointer', '&:hover': { color: 'secondary.main' } }}
                          onClick={() => setEditingVideoId(videoFile.id)}
                          title={tt('Düzenlemek için tıklayın', 'Click to edit')}
                        >
                          {videoFile.name || `${tt('Video', 'Video')} ${index + 1}`}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {formatFileSize(videoFile.file.size)} | MP4
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setEditingVideoId(videoFile.id)}
                        disabled={editingVideoId === videoFile.id}
                      >
                        {tt('Düzenle', 'Edit')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => removeVideo(videoFile.id)}
                      >
                        {tt('Kaldır', 'Remove')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {selectedVideos.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
            <VideoLibraryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {tt('Henüz video seçilmedi', 'No video selected yet')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tt('Yukarıdaki alanı kullanarak video seçebilirsiniz.', 'You can select videos using the area above.')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={uploading} variant="outlined">
          {tt('İptal', 'Cancel')}
        </Button>
        <Button
          onClick={handleUpload}
          disabled={selectedVideos.length === 0 || uploading}
          variant="contained"
          color="secondary"
          startIcon={uploading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {uploading ? tt('Yükleniyor...', 'Uploading...') : `${selectedVideos.length} ${tt('Video Yükle', 'Upload Video')}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoUploadModal;
